<template>
  <div
    class="flex flex-col items-center w-full bg-contrast-background h-full rounded-lg overflow-hidden mb-16 md:w-[430px] m-auto"
  >
    <div class="w-full px-5 py-5">
      <div class="bg-white border border-gray-200 rounded-small p-4 mb-4">
        <div class="text-xs uppercase text-gray-400 font-bold mb-2">
          {{ $t("reservations.location") }}
        </div>
        <div class="font-bold text-blue-900">{{ currentLocation.name }}</div>
        <div class="text-gray-400 text-sm">{{ currentLocation.address }}</div>
      </div>

      <div class="mb-5">
        <div
          class="text-gray-400 text-xs font-bold uppercase tracking-[0.04em] mb-2"
        >
          {{ $t("reservations.party-size") }}
        </div>
        <div class="grid grid-cols-5 gap-2">
          <button
            v-for="size in partySizes"
            :key="size"
            class="h-11 rounded-small font-bold border"
            :class="
              partySize === size ? '' : 'bg-white text-blue-900 border-gray-200'
            "
            :style="selectedChoiceStyle(partySize === size)"
            @click="partySize = size"
          >
            {{ size }}
          </button>
        </div>
      </div>

      <div v-if="roomOptions.length > 1" class="mb-5">
        <div
          class="text-gray-400 text-xs font-bold uppercase tracking-[0.04em] mb-2"
        >
          {{ $t("reservations.area") }}
        </div>
        <div class="flex flex-row overflow-x-auto space-x-3 pb-1">
          <button
            v-for="room in roomOptions"
            :key="room.id || 'any'"
            class="min-w-[90px] rounded-small border px-3 py-3 text-sm font-semibold"
            :class="
              selectedRoomId === room.id
                ? ''
                : 'bg-white text-blue-900 border-gray-200'
            "
            :style="selectedChoiceStyle(selectedRoomId === room.id)"
            @click="selectedRoomId = room.id"
          >
            {{ room.name }}
          </button>
        </div>
      </div>

      <div class="my-16 scale-125 flex flex-col items-center">
        <n-date-picker
          v-model:value="selectedDay"
          panel
          :is-date-disabled="dateIsDisabled"
          :first-day-of-week="0"
          :actions="[]"
        />
      </div>

      <div class="mb-5">
        <div
          class="text-gray-400 text-xs font-bold uppercase tracking-[0.04em] mb-2"
        >
          {{ $t("reservations.time") }}
        </div>
        <div v-if="loadingAvailability" class="flex justify-center py-4">
          <c-loading-spinner />
        </div>
        <div v-else-if="availableGroups.length" class="space-y-4">
          <div v-for="group in availableGroups" :key="group.label">
            <div class="font-bold text-blue-900 text-sm mb-2">
              {{ group.label }}
            </div>
            <div class="grid grid-cols-3 gap-2">
              <button
                v-for="slot in group.slots"
                :key="`${group.label}-${slot.time}`"
                class="py-3 rounded-small border font-semibold"
                :class="slotClass(slot)"
                :style="slotStyle(slot)"
                :disabled="!slot.available"
                @click="selectedSlot = slot.time"
              >
                {{ slot.time }}
              </button>
            </div>
          </div>
        </div>
        <div v-else class="text-center text-gray-400 font-semibold py-5">
          {{ $t("reservations.no-slots") }}
        </div>
      </div>

      <div class="mb-5">
        <div
          class="text-gray-400 text-xs font-bold uppercase tracking-[0.04em] mb-2"
        >
          {{ $t("reservations.comment") }}
        </div>
        <n-input
          v-model:value="comment"
          type="textarea"
          :placeholder="$t('reservations.comment-placeholder')"
        />
      </div>

      <div class="bg-white border border-gray-200 rounded-small p-4 mb-5">
        <div class="flex items-center gap-2 text-blue-900">
          <c-icon name="EventAvailableRound" size="1.2rem" />
          <div class="text-sm">{{ summaryText }}</div>
        </div>
      </div>

      <button
        class="w-full rounded-small py-4 font-bold text-lg mb-5 border"
        :class="selectedSlot ? '' : 'bg-gray-200 text-gray-400 border-gray-200'"
        :style="selectedSlot ? selectedChoiceStyle(true) : null"
        :disabled="!selectedSlot"
        @click="bookReservation"
      >
        {{ $t("reservations.reserve-table") }}
      </button>
    </div>
  </div>
</template>

<script>
import api from "@/api.js";
import { mapActions, mapState } from "pinia";
import { authStore } from "../stores/auth";
import moment from "moment";
import CIcon from "../../../core-ui/src/components/CIcon.vue";
import CLoadingSpinner from "../../../core-ui/src/components/CLoadingSpinner.vue";
import googleConversion from "@/googleConversion.js";
import utils from "@comeback/core/src/utils.js";

export default {
  name: "ReservationPage",
  components: {
    CIcon,
    CLoadingSpinner,
  },
  data() {
    return {
      rooms: [],
      closedDays: [],
      config: {
        maxPartySize: 9,
        futureBookingDays: 90,
      },
      availability: [],
      partySize: 2,
      selectedRoomId: null,
      selectedDay: new Date().getTime(),
      selectedSlot: null,
      comment: "",
      loadingAvailability: false,
    };
  },
  async mounted() {
    googleConversion.parseAndStoreToken();
    await this.refreshSetup();
    await this.refreshAvailability();
  },
  computed: {
    ...mapState(authStore, [
      "organization",
      "currentLocation",
      "currentCustomer",
      "reservation",
      "branding",
    ]),
    partySizes() {
      let maxPartySize = Math.max(1, Number(this.config.maxPartySize) || 9);
      return Array.from(
        { length: Math.min(maxPartySize, 30) },
        (_, index) => index + 1
      );
    },
    roomOptions() {
      return [
        { id: null, name: this.$t("reservations.any-area") },
        ...this.rooms,
      ];
    },
    availableGroups() {
      return this.availability
        .map((group) => ({
          ...group,
          slots: group.slots.filter((slot) => slot.available),
        }))
        .filter((group) => group.slots.length > 0);
    },
    selectedDateKey() {
      return moment(this.selectedDay).format("YYYY-MM-DD");
    },
    summaryText() {
      if (!this.selectedSlot) return this.$t("reservations.pick-date-time");
      return this.$t("reservations.summary", {
        partySize: this.partySize,
        date: moment(this.selectedDay).format("DD/MM"),
        time: this.selectedSlot,
      });
    },
    buttonColor() {
      let accentColor = this.branding?.accentColor || "#545383";
      let backgroundColor = this.branding?.backgroundColor || "#FFFFFF";
      if (utils.colorIsDark(accentColor)) return accentColor;
      return utils.colorIsDark(backgroundColor) ? backgroundColor : accentColor;
    },
    buttonTextColor() {
      return utils.colorIsDark(this.buttonColor) ? "#FFFFFF" : "#222134";
    },
    buttonBorderColor() {
      return utils.colorIsDark(this.buttonColor) ? this.buttonColor : "#222134";
    },
  },
  methods: {
    ...mapActions(authStore, ["updateReservation"]),
    async refreshSetup() {
      let response = await api.get(
        `/reservations/location/${this.currentLocation.id}/setup`
      );
      this.rooms = response.data.rooms || [];
      this.closedDays = response.data.closedDays || [];
      this.config = {
        ...this.config,
        ...(response.data.config || {}),
      };
      if (this.partySize > this.config.maxPartySize) {
        this.partySize = this.config.maxPartySize;
      }
    },
    async refreshAvailability() {
      this.loadingAvailability = true;
      this.selectedSlot = null;
      try {
        let response = await api.get(
          `/reservations/location/${this.currentLocation.id}/availability`,
          {
            params: {
              date: this.selectedDateKey,
              partySize: this.partySize,
              roomId: this.selectedRoomId,
            },
          }
        );
        this.availability = response.data || [];
        let firstSlot = this.availableGroups[0]?.slots[0]?.time;
        this.selectedSlot = firstSlot || null;
      } finally {
        this.loadingAvailability = false;
      }
    },
    slotClass(slot) {
      if (!slot.available) return "bg-gray-200 text-gray-400 border-gray-200";
      if (this.selectedSlot === slot.time) {
        return "";
      }
      return "bg-white text-blue-900 border-gray-200";
    },
    slotStyle(slot) {
      return this.selectedChoiceStyle(
        slot.available && this.selectedSlot === slot.time
      );
    },
    selectedChoiceStyle(selected) {
      if (!selected) return null;
      return {
        backgroundColor: this.buttonColor,
        borderColor: this.buttonBorderColor,
        color: this.buttonTextColor,
      };
    },
    dateIsDisabled(timestamp) {
      let today = moment().startOf("day").valueOf();
      if (timestamp < today) return true;
      let lastBookableDay = moment()
        .startOf("day")
        .add(Number(this.config.futureBookingDays) || 90, "days")
        .valueOf();
      if (timestamp > lastBookableDay) return true;
      let dateKey = moment(timestamp).format("YYYY-MM-DD");
      return this.closedDays.some((closedDay) =>
        this.closedDayMatchesDate(closedDay, dateKey)
      );
    },
    closedDayMatchesDate(closedDay, dateKey) {
      let startDate = closedDay.startDate;
      let endDate = closedDay.endDate || closedDay.startDate;
      if (!startDate || !endDate || !dateKey) return false;
      if (closedDay.yearly) {
        let dayMonth = dateKey.slice(5);
        let startDayMonth = startDate.slice(5);
        let endDayMonth = endDate.slice(5);
        if (startDayMonth <= endDayMonth) {
          return dayMonth >= startDayMonth && dayMonth <= endDayMonth;
        }
        return dayMonth >= startDayMonth || dayMonth <= endDayMonth;
      }
      return dateKey >= startDate && dateKey <= endDate;
    },
    async bookReservation() {
      if (!this.selectedSlot) return;
      let reservation = {
        partySize: this.partySize,
        roomId: this.selectedRoomId,
        startTime: moment(this.selectedDay)
          .set({
            hour: Number(this.selectedSlot.split(":")[0]),
            minute: Number(this.selectedSlot.split(":")[1]),
            second: 0,
            millisecond: 0,
          })
          .toDate(),
        comment: this.comment,
      };
      this.updateReservation(reservation);
      if (this.currentCustomer?.id) {
        try {
          await api.post(
            `/reservations/location/${this.currentLocation.id}/reservation`,
            { ...reservation, customerId: this.currentCustomer.id }
          );
          googleConversion.sendConversionEvent();
          this.$router.push({
            name: "downloadPage",
            params: { ...this.$route.params },
          });
        } catch {
          this.$notificationGenerator.error(
            this.$t("reservations.no-availability")
          );
          await this.refreshAvailability();
        }
      } else {
        this.$router.push({
          name: "createCustomerPage",
          params: { ...this.$route.params },
        });
      }
    },
  },
  watch: {
    partySize() {
      this.refreshAvailability();
    },
    selectedRoomId() {
      this.refreshAvailability();
    },
    selectedDay() {
      this.refreshAvailability();
    },
  },
};
</script>
